/**
 * Rótulo dinâmico da contraparte do escopo de exceção.
 *
 * Entrada ('E') → "Fornecedor" (mercadoria vem de um fornecedor)
 * Saída  ('S') → "Cliente"     (mercadoria sai para um cliente)
 *
 * Mantém os códigos `tipo` de escopo inalterados (contrib_icms, regime_trib,
 * tipo_tributario, etc.); apenas o texto exibido é computado em runtime.
 */
import { TipoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-escopo.enum';
import { TipoGrupoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-grupo-escopo.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';

export type RotuloContraparte = 'Cliente' | 'Fornecedor';

export function rotuloContraparte(tipoOperacao: TipoOperacaoEnum | null | undefined): RotuloContraparte {
    return tipoOperacao === TipoOperacaoEnum.Entrada ? 'Fornecedor' : 'Cliente';
}

export interface EscopoTipoDef {
    tipo: TipoEscopoEnum;
    label: string;
    placeholder: string;
}

export interface EscopoGrupoDef {
    grupo: TipoGrupoEscopoEnum;
    labelGrupo: string;
    tipos: EscopoTipoDef[];
}

export type TipoEscopoLabelMap = Record<number, string>;

export function iconeEscopoGrupo(grupo: TipoGrupoEscopoEnum): string {
    switch (grupo) {
        case TipoGrupoEscopoEnum.Territoriais: return 'public';
        case TipoGrupoEscopoEnum.Produto: return 'inventory_2';
        case TipoGrupoEscopoEnum.Servico: return 'design_services';
        case TipoGrupoEscopoEnum.Fornecedor: return 'local_shipping';
        case TipoGrupoEscopoEnum.Cliente: return 'person_search';
    }
    return 'tune';
}

export function iconeTipoEscopo(tipo: TipoEscopoEnum): string {
    switch (tipo) {
        case TipoEscopoEnum.UfOrigem:
        case TipoEscopoEnum.UfDestino: return 'public';
        case TipoEscopoEnum.Produto: return 'inventory_2';
        case TipoEscopoEnum.Ncm: return 'category';
        case TipoEscopoEnum.Cest: return 'sell';
        case TipoEscopoEnum.OrigemMercadoria: return 'flag';
        case TipoEscopoEnum.TipoItem: return 'list_alt';
        case TipoEscopoEnum.Servico: return 'design_services';
        case TipoEscopoEnum.Nbs: return 'receipt_long';
        case TipoEscopoEnum.CodigoTribNacional: return 'qr_code';
        case TipoEscopoEnum.IndicadorInscricaoEstadual: return 'person_search';
        case TipoEscopoEnum.ContribIbsCbs: return 'person_search';
        case TipoEscopoEnum.RegimeTrib: return 'account_balance';
        case TipoEscopoEnum.TipoTributario: return 'badge';
    }
    return 'tune';
}

/**
 * Recebe a definição base de grupos de escopo (com o grupo "Destinatário")
 * e devolve uma cópia com o rótulo da contraparte aplicado ao grupo e aos
 * itens "Regime/Tipo Tributário do …" conforme o tipo de operação.
 */
export function aplicarRotuloContraparte<T extends EscopoGrupoDef>(
    defs: readonly T[],
    tipoOperacao: TipoOperacaoEnum | null | undefined,
    tipoEscopoLabels: TipoEscopoLabelMap = {},
): T[] {
    const contraparte = rotuloContraparte(tipoOperacao);
    return defs.map(g => {
        if (g.grupo !== TipoGrupoEscopoEnum.Cliente && g.grupo !== TipoGrupoEscopoEnum.Fornecedor) {
            return {
                ...g,
                tipos: g.tipos.map(t => ({
                    ...t,
                    label: tipoEscopoLabels[t.tipo] ?? t.label,
                })),
            };
        }
        return {
            ...g,
            grupo: contraparte === 'Fornecedor' ? TipoGrupoEscopoEnum.Fornecedor : TipoGrupoEscopoEnum.Cliente,
            labelGrupo: contraparte,
            tipos: g.tipos.map(t => ({
                ...t,
                label:
                    t.tipo === TipoEscopoEnum.RegimeTrib || t.tipo === TipoEscopoEnum.TipoTributario
                        ? `${tipoEscopoLabels[t.tipo] ?? t.label} do ${contraparte}`
                        : tipoEscopoLabels[t.tipo] ?? t.label,
            })),
        };
    }) as T[];
}

/** Mapa tipoEscopo → rótulo de exibição, já com a contraparte resolvida (para badges). */
export function escopoLabels(
    defs: readonly EscopoGrupoDef[],
    tipoOperacao: TipoOperacaoEnum | null | undefined,
    tipoEscopoLabels: TipoEscopoLabelMap = {},
): Record<number, string> {
    const labels: Record<number, string> = {};
    aplicarRotuloContraparte(defs, tipoOperacao, tipoEscopoLabels).forEach(g =>
        g.tipos.forEach(t => { labels[t.tipo] = t.label; }),
    );
    return labels;
}
